import { useEffect, useState } from 'react';

import type { HubItem, HubItemKind } from '../api/types';

import { fetchHubItems } from '../api/infoHubApi';

export type HubArchiveState = 'loading' | 'ready' | 'error';

export interface UseHubArchiveResult {
  items: HubItem[];
  total: number;
  state: HubArchiveState;
}

// Both surfaces are low-volume today and are filtered client-side (news by search text,
// articles by category), so the whole active set comes back in one call — the same trade the
// newsletter archive makes. Revisit with real paging when either outgrows this.
const ARCHIVE_PAGE_SIZE = 500;

/**
 * Loads the active article library or news feed, newest first. `limit` caps how many come
 * back — the landing page only wants the latest few, the archive wants the lot.
 *
 * `categoryId` narrows to one category server-side. The reading view's related list uses it;
 * an empty string means no filter, which is what an item with no category of its own wants.
 *
 * `notfound` isn't a state here: an empty library is a valid `ready` result, not an error.
 * `total` is the server-side count, so a limited call can still say how many there are.
 */
export const useHubArchive = (
  kind: HubItemKind,
  limit: number = ARCHIVE_PAGE_SIZE,
  categoryId?: string,
): UseHubArchiveResult => {
  const [items, setItems] = useState<HubItem[]>([]);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState<HubArchiveState>('loading');

  useEffect(() => {
    let cancelled = false;
    setState('loading');

    const load = async (): Promise<void> => {
      try {
        const result = await fetchHubItems(kind, {
          page: 1,
          pageSize: limit,
          categoryId: categoryId || undefined,
        });
        if (cancelled) {
          return;
        }
        setItems(result.items);
        setTotal(result.total);
        setState('ready');
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('Error loading hub archive:', error);
        setState('error');
      }
    };

    load();
    return (): void => {
      cancelled = true;
    };
  }, [kind, limit, categoryId]);

  return { items, total, state };
};
